/**
 * AutosaveChip — a small floating "✓ saved 2s ago" pill.
 *
 *   • Persists the form to localStorage debounced 600 ms after every change.
 *   • Only restores on the same browser; cleared when the user submits.
 *   • Encrypted SSN ciphertext + ssn_last4 are kept (it's the encrypted blob
 *     anyway, and matches what they already typed).  Uploaded attachment
 *     IDs are also restored — they remain valid for an hour server-side.
 */
import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, Cloud } from '@phosphor-icons/react';

const KEY = 'cs_onboarding_draft_v1';

export function loadDraft() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export function clearDraft() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

export function AutosaveChip({ form }) {
  const [savedAt, setSavedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const tRef = useRef(null);

  /* Debounced save */
  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => {
      try {
        localStorage.setItem(KEY, JSON.stringify(form));
        setSavedAt(Date.now());
      } catch { /* quota — ignore */ }
    }, 600);
    return () => { if (tRef.current) clearTimeout(tRef.current); };
  }, [form]);

  /* Tick "x seconds ago" relative time */
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, []);

  if (!savedAt) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider"
        style={{ color: 'var(--cs-text-muted)' }}
        data-testid="autosave-chip">
        <Cloud size={10} weight="fill" /> auto-save on
      </span>
    );
  }
  const sec = Math.max(0, Math.floor((now - savedAt) / 1000));
  const label = sec < 5 ? 'just now' : sec < 60 ? `${sec}s ago` : `${Math.floor(sec / 60)}m ago`;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider"
      style={{ color: '#10B981' }}
      data-testid="autosave-chip">
      <CheckCircle size={10} weight="fill" /> saved {label}
    </span>
  );
}
